/**
 * Property Utilities
 * 
 * Helper functions for consistent property data handling.
 * Handles CRM field mapping (hotel_name → property_name).
 */

/**
 * Get property name with fallback
 * CRM uses 'hotel_name', Superhost uses 'property_name'
 * This function ensures backward compatibility
 * 
 * @param {Object} property - Property object from API
 * @returns {string} - Property name
 */
export const getPropertyName = (property) => {
  if (!property) return '';
  return property.property_name || property.hotel_name || property.name || property.propertyName || '';
};

/**
 * Get owner name with fallback
 * 
 * @param {Object} property - Property object
 * @returns {string} - Owner name
 */
export const getOwnerName = (property) => {
  if (!property) return '';
  return property.owner_name || property.ownerName || '';
};

/**
 * Normalize property object to use consistent field names
 * Call this when receiving data from CRM
 * 
 * @param {Object} crmProperty - Property from CRM
 * @returns {Object} - Normalized property
 */
export const normalizeProperty = (crmProperty) => {
  if (!crmProperty) return null;
  
  const propertyName = getPropertyName(crmProperty);
  const ownerName = getOwnerName(crmProperty);
  
  return {
    ...crmProperty,
    // Primary fields (use these in UI)
    property_name: propertyName,
    owner_name: ownerName,
    // Backward compatibility (keep these too)
    hotel_name: propertyName,
    name: propertyName,
    // Normalized fields
    total_rooms: crmProperty.total_rooms || crmProperty.totalRooms || 0,
    star_rating: crmProperty.star_rating || crmProperty.starRating || 0,
    property_type: crmProperty.property_type || crmProperty.propertyType || 'Hotel',
  };
};

/**
 * Normalize array of properties
 * 
 * @param {Array} properties - Array of properties from CRM
 * @returns {Array} - Normalized properties
 */
export const normalizeProperties = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.map(normalizeProperty).filter(Boolean);
};

/**
 * Get display name for property (for UI)
 * Format: "Property Name, City" or just "Property Name"
 * 
 * @param {Object} property - Property object
 * @param {boolean} includeCity - Include city in display
 * @returns {string} - Display name
 */
export const getPropertyDisplayName = (property, includeCity = false) => {
  if (!property) return '';
  const name = getPropertyName(property);
  if (includeCity && property.city) {
    return `${name}, ${property.city}`;
  }
  return name;
};

export default {
  getPropertyName,
  getOwnerName,
  normalizeProperty,
  normalizeProperties,
  getPropertyDisplayName,
};
